import React, { useCallback, useEffect, useRef, useState } from "react";
import { IFundSection } from "./SecondaryNav.types";
import { forceSetOverview, setSecondaryNavState } from "./setSecondaryNavState";
import debounce from "lodash/debounce";
import { jumpToSection } from "./jumpToSection";
import { HeaderHeightEvent } from "./SecondaryNav";
import { isMobileDevice } from '../../../utils/window-utils';

type IWindowDimentions = {
  height: any;
  width: any;
};

type IOffsetMargins = {
  negativeMarginTop: string;
  negativeMarginBottom: string;
};

/**
 * Hook that observes all of the sections and changes a class based on if it is intersects the bottom of the sticky header.
 * @param Sections Array of fund sections.
 * @param setSections StateSetter for FundSections.
 */
export function useInViewObserver(
  Sections: IFundSection[],
  setSections: React.Dispatch<React.SetStateAction<IFundSection[]>>
) {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const [windowDimentions, setWindowDimentions] = useState<IWindowDimentions>({
    height: window.innerHeight,
    width: window.innerWidth,
  });

  const [rootOffsetMargins, setrootOffsetMargins] = useState(
    getRootoffSetMargins()
  );

  //effect to set offsetmargins upon resize.
  useEffect(
    function () {
      const { negativeMarginTop, negativeMarginBottom } = rootOffsetMargins;

      //intersection observer options
      let options = {
        root: null,
        rootMargin: `${negativeMarginTop} 0px ${negativeMarginBottom} 0px`,
        threshold: 0,
      };

      observerRef.current = new IntersectionObserver(function (
        entries,
        observer
      ) {
        const SectionsAlreadyActive = Array.from(
          document.querySelectorAll(".sidebar-nav__item.active")
        ).map((x) => (x as HTMLElement).dataset.target);
        const FilteredSectionsAlreadyActive = SectionsAlreadyActive.filter(
          (x) => x != undefined
        ) as string[];
        entries.forEach((entry) => {
          const elementId = entry.target.id;
          const section = Sections.find((x) => x.id === elementId);
          if (!section) return;
          if (entry.isIntersecting) {
            //unset all existing items
            if (SectionsAlreadyActive.length > 0)
              setSecondaryNavState(
                Sections,
                setSections,
                FilteredSectionsAlreadyActive,
                ""
              );
            //set the new one
            setSecondaryNavState(Sections, setSections, [elementId], "active");
          }
        });
      },
      options);

      //Observe each targeted element.
      Sections.forEach((Section) => {
        const targettedElement = document.getElementById(Section.id as string);
        if (!targettedElement) return;
        observerRef.current?.observe(targettedElement);
      });

      //sets overview if nothing is set. //bug with browsers where Intersection Observer does not load the first item.
      forceSetOverview(Sections, setSections);

      return function () {
        //On unmount, remove observer;
        observerRef.current?.disconnect();
      };
    },
    [rootOffsetMargins] //The hook is called everytime the brower is resized.
  );

  useEffect(() => {
      const updateHeaderHeight = debounce(() => {
          const header = document.querySelector(".root .site-header");
          const stickyWrapper = document.querySelector(".root .sticky-wrapper") as HTMLElement;

          if (header && stickyWrapper) {
              const headerHeight = Math.round(header.getBoundingClientRect().height);
              header.getBoundingClientRect();
              stickyWrapper.style.height = `${headerHeight}px`;

              setrootOffsetMargins(getRootoffSetMargins());
          }
      }, 300);

      window.addEventListener('resize', updateHeaderHeight);
      
      return () => {
          window.removeEventListener('resize', updateHeaderHeight);
      };
  }, []);

  //listen for window resizes
  useEffect(() => {
    const resizeHandler = debounce(function () {
      if (
        windowDimentions.width != window.innerWidth ||
        windowDimentions.height != window.innerHeight
      ) {
        setWindowDimentions((windowDimentions) => {
          return {
            ...windowDimentions,
            height: window.innerHeight,
            width: window.innerWidth,
          };
        });

        //Trigger a root offset margin reset. Recalculate and reset the intersection observer.
        setrootOffsetMargins(getRootoffSetMargins());
      }
    }, 500);

    window.addEventListener("resize", resizeHandler);

    return () => {
      window.removeEventListener("resize", resizeHandler);
    };
  }, []);

  //now listen to alertbar changes
  const headerHeightEventHandler = useCallback((e: HeaderHeightEvent) => {
    //Trigger a root offset margin reset. Recalculate and reset the intersection observer.
    setrootOffsetMargins(getRootoffSetMargins());
  }, []);

  //now trigger it once incase anything messes up during the initial load.
  useEffect(() => {
    window.setTimeout((x:any) => setrootOffsetMargins(getRootoffSetMargins()), 500);
  
  }, [])
  

  useEffect(() => {
    //Listener to listen to any alert bars being added.
    document.addEventListener("headerHeightEvent", (e) =>
      headerHeightEventHandler(e)
    );

    return () => {
      document.removeEventListener("headerHeightEvent", (e) =>
        headerHeightEventHandler(e)
      );
    };
  }, []);

  useEffect(function () {
    const popStateHandler = function (event: PopStateEvent) {
      const newState = event.state?.section ?? false;
      if (!newState || typeof newState != "string") return;
      window.setTimeout(() => jumpToSection(newState), 0);
    };

    window.addEventListener("popstate", popStateHandler);

    return () => {
      window.removeEventListener("popstate", popStateHandler);
    };
  }, []);

  useEffect(() => {
      let cleanup: (() => void) | undefined;

      const initObservers = () => {
        const stickyLeftSideBar = document.querySelector(
          ".sticky-left-sidebar"
        ) as HTMLElement | null;
        const sidebarNav = document.querySelector(".sidebar-nav") as HTMLElement | null;
        const footer = document.querySelector("footer");

        if (!stickyLeftSideBar || !sidebarNav || !footer) {
          return false;
        }

        let sidebarNavBottom: number;
        let footerTop: number;
        let isStickyFixed = false;

        const observerOptions = {
          root: null,
          rootMargin: "0px",
          threshold: [0, 0.1]
        };

        const updateStickyState = () => {
          const shouldBeStickyFixed = sidebarNavBottom >= footerTop;

          if (shouldBeStickyFixed && !isStickyFixed) {
            stickyLeftSideBar.classList.add("sticky-fixed");
            isStickyFixed = true;
          } else if (!shouldBeStickyFixed && isStickyFixed) {
            stickyLeftSideBar.classList.remove("sticky-fixed");
            isStickyFixed = false;
          }
        };

        const sidebarObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            sidebarNavBottom = entry.boundingClientRect.bottom + window.scrollY;
            updateStickyState();
          });
        }, observerOptions);

        const footerObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            footerTop = entry.boundingClientRect.top + window.scrollY;
            updateStickyState();
          });
        }, observerOptions);

        sidebarObserver.observe(sidebarNav);
        footerObserver.observe(footer);

        const initializeValues = () => {
          sidebarNavBottom = sidebarNav.getBoundingClientRect().bottom + window.scrollY;
          footerTop = footer.getBoundingClientRect().top + window.scrollY;
          updateStickyState();
        };

        initializeValues();

        window.addEventListener('resize', initializeValues);
        window.addEventListener('scroll', initializeValues);

        cleanup = () => {
          sidebarObserver.disconnect();
          footerObserver.disconnect();
          window.removeEventListener('resize', initializeValues);
          window.removeEventListener('scroll', initializeValues);
        };

        return true;
      };

      const portalWrapper = document.getElementById('sidebar-portal-wrapper');
      
      if (portalWrapper) {
        const mutationObserver = new MutationObserver(() => {
          if (initObservers()) {
            mutationObserver.disconnect();
          }
        });

        mutationObserver.observe(portalWrapper, {
          childList: true,
          subtree: true
        });

        if (!initObservers()) {
        } else {
          mutationObserver.disconnect();
        }

        return () => {
          mutationObserver.disconnect();
          cleanup?.();
        };
      } else {
        const timeoutId = setTimeout(initObservers, 200);
        return () => {
          clearTimeout(timeoutId);
          cleanup?.();
        };
      }
    }, []);
}

export function getRootoffSetMargins() :IOffsetMargins {
  const HeaderHeight = Math.round(
    document.querySelector(".root .sticky-wrapper")?.getBoundingClientRect().height ?? 0
  );
  const stickyHeader = document.querySelector(".root .sticky-header");
  const fundLabel = stickyHeader?.querySelector('.inner-container-mobile .fund-label');
  const fundLabelHeight = Math.round(fundLabel?.getBoundingClientRect().height ?? 0);

  const offsetFromBottomOfHeader = 90 + (!isMobileDevice() ? 30 : fundLabelHeight ? fundLabelHeight - 5 : 40);
  const VisibleScrollableHeight = window.innerHeight - (HeaderHeight + offsetFromBottomOfHeader); // (minus offset)
  const negativeMarginTop = (HeaderHeight + offsetFromBottomOfHeader) * -1 + "px";
  const negativeMarginBottom = (VisibleScrollableHeight - 2) * -1 + "px";
  return { negativeMarginTop, negativeMarginBottom };
}

